import { createSelector } from '@ngrx/store';
import { selectAvailabilityFeatureState } from '../reducers';
import { FilghtAvailability } from '@models/availability.model';
import { selectAppCmsCopy, selectFlightsCopy } from './cms.selector';
import { selectCurrentCultureCode } from './culture.selector';
import { countAvailabilityPassengersByType, countAvailabilityPassengersExcludingType, createFlightAvailability } from '@utils/availability.utils';
import { DateTime } from 'luxon';
import { DATE_FORMAT_YEAR_MONTH_DATE } from '@constants/date-format.constants';
import { PassengerTypeCode } from '@models/booking.models';
import { selectBooking, selectJourneys } from "@selectors/booking.selector";
import { selectStationsState } from './station.selector';
import { PREMIUM_CABIN, routeDiscountMap } from "@constants/app.constants";
import { selectActiveSegment } from './seat.selector';

export const selectAvailabilityAvailabilityState = createSelector(
  selectAvailabilityFeatureState,
  (state) => state.availability,
);

export const selectAvailabilityLowFareAvailabilityState = createSelector(
  selectAvailabilityFeatureState,
  (state) =>
    state.lowFareAvailability?.map((trip) => {
      return {
        ...trip,
        cabins: trip.cabins
          .slice()
          .sort((a, b) =>
            a.cabinName > b.cabinName ? 1 : b.cabinName > a.cabinName ? -1 : 0,
          ),
      };
    }),
);

export const selectStationsFromDestinationMap = createSelector(
  selectAvailabilityFeatureState,
  (state) => state.stationsFromDestinationMap,
);

export const selectAvailabilitySelectedState = createSelector(
  selectAvailabilityFeatureState,
  (state) => state.selection,
);

export const selectAvailabilitySelectedTripsState = createSelector(
  selectAvailabilityFeatureState,
  (state) => state.selection?.sellKeys,
);

export const selectAvailabilitySelectedJourneysState = createSelector(
  selectAvailabilityFeatureState,
  (state) => state.selection?.journeys,
);

export const selectAvailabilitySelectedFaresState = createSelector(
  selectAvailabilitySelectedTripsState,
  selectAvailabilitySelectedJourneysState,
  (sellKeys, selectedJourneys) => {
    return sellKeys?.map((key, i) => {
      return selectedJourneys?.[i]?.fares.find(
        (fare) => fare.fareAvailabilityKey === key?.fareAvailabilityKey,
      );
    });
  },
);

export const selectAvailabilityTripTypeState = createSelector(
  selectAvailabilityFeatureState,
  (state) => state.tripType,
);

export const selectAvailabilityDisplayViewState = createSelector(
  selectAvailabilityFeatureState,
  (state) => state.displayView,
);

export const selectAutoSelectState = createSelector(
  selectAvailabilityFeatureState,
  (state) => state.autoSelect,
);

export const selectAvailabilityIsSearchByQuery = createSelector(
  selectAvailabilityFeatureState,
  (state) => state.isSearchByQuery,
);

export const selectHasInvalidPromoCode = createSelector(
  selectAvailabilityFeatureState,
  (state) => state.hasInvalidPromoCode,
);

export const selectAvailabilitySearchInputState = createSelector(
  selectAvailabilityFeatureState,
  (state) => state.searchAvailabilityInput,
);

export const selectAvailabilitySearchInputCriteriaState = createSelector(
  selectAvailabilityFeatureState,
  (state) => state.searchAvailabilityInput?.criteria,
);

export const selectSearchInputState = createSelector(
  selectAvailabilityFeatureState,
  (state) => state.searchInput,
);

export const selectSearchInputCriteriaState = createSelector(
  selectSearchInputState,
  (searchInput) => searchInput?.criteria,
);

export const selectSearchInputPassengerState = createSelector(
  selectSearchInputState,
  (searchInput) => searchInput?.passengers,
);

export const selectAvailabilitySourceOrganization = createSelector(
  selectAvailabilityFeatureState,
  (availability) => availability.sourceOrganization,
);

export const selectAvailabilityInfantInLapFee = createSelector(
  selectAvailabilityFeatureState,
  (state) => state.availability?.infantInLapFee,
);

export const selectAvailabilityRedirectId = createSelector(
  selectAvailabilityFeatureState,
  (availability) => availability.redirectId,
);

export const selectRouteSchedule = createSelector(
  selectAvailabilityFeatureState,
  (state) => state.routeSchedule,
);

export const selectRecentScheduleNotFound = createSelector(
  selectAvailabilityFeatureState,
  (state) => state.recentScheduleNotFound,
);

/* istanbul ignore next  */
export const selectCoverGeniusQuote = createSelector(
  selectAvailabilityFeatureState,
  (state) => state.coverGenius,
);

export const selectShowLowFareSelectedOutboundDate = createSelector(
  selectAvailabilityFeatureState,
  (state) => state.showLowFareSelectedOutboundDate,
);

export const selectShowLowFareSelectedReturnDate = createSelector(
  selectAvailabilityFeatureState,
  (state) => state.showLowFareSelectedReturnDate,
);

export const selectPreferredCabin = createSelector(
  selectAvailabilityFeatureState,
  (state) => state.preferredCabinClass,
);

export const selectSelectedCabinClass = createSelector(
  selectAvailabilityFeatureState,
  (state) => state.selectedCabinClass,
);

export const selectCabinClass = createSelector(
  selectAvailabilityFeatureState,
  (state) => state.selectedCabinClass ?? state.preferredCabinClass
)

export const selectLowFaresByOrigin = createSelector(selectAvailabilityFeatureState, (state) => state.lowFareByOrigin);

export const selectLowFaresByDestination = createSelector(selectAvailabilityFeatureState, (state) => state.lowFareByDestination);

export const selectSelectedDates = createSelector(
  selectAvailabilityFeatureState,
  (state) => state.selectedDates,
);

export const selectActiveWeek = createSelector(
  selectAvailabilityFeatureState,
  (state) => state.activeWeek,
);

export const selectAllJourneysSelected = createSelector(
  selectAvailabilitySelectedJourneysState,
  selectAvailabilitySearchInputState,
  (journeys, availabilitySearch) =>
    journeys?.filter((journey) => journey !== null).length ===
    availabilitySearch?.criteria.length,
);

export const selectJourneysConfirmed = createSelector(
  selectAvailabilitySelectedJourneysState,
  selectJourneys,
  (journeys, journeysInBooking) => {
    if (!journeys || !journeysInBooking) {
      return false;
    }

    if (journeys.length !== journeysInBooking.length) {
      return false;
    }

    return journeysInBooking.every((journeyInBooking) =>
      journeys.some((journey) => journeyInBooking.journeyKey === journey?.journeyKey),
    );
  }
)

export const selectFlightsAvailability = createSelector(
  selectAvailabilityFeatureState,
  selectFlightsCopy,
  selectCurrentCultureCode,
  selectSelectedDates,
  selectRouteSchedule,
  selectStationsState,
  selectAvailabilityTripTypeState,
  (state, flightsCopy, cultureCode, selectedDates, routeSchedule, stations, tripType) => {
    if (state.searchInput && state.lowFareAvailability && flightsCopy && cultureCode){
      const flightAvailabilities: FilghtAvailability[] = [];
      const cabinClass = state.selectedCabinClass ?? state.preferredCabinClass;

      state.searchInput.criteria.forEach((criteria, index) => {
        const routeDates = routeSchedule?.[criteria.origin + '-' + criteria.destination]?.filter(s => s.isDohop === false) ?? [];
        const lowFareAmounts = state.lowFareAvailability?.[index]?.cabins.filter(c => c.cabinName.startsWith(cabinClass.toString()))[0]?.lowFareAmounts ?? [];
        const flightAvailability: FilghtAvailability = createFlightAvailability(index, flightsCopy, selectedDates, lowFareAmounts, cultureCode, criteria, cabinClass, routeDates, stations, tripType);
        flightAvailabilities.push(flightAvailability);
      });
      return flightAvailabilities
    }
    return null
  }
)

export const selectFlightsAvailabilityOneType = createSelector(
  selectAvailabilityFeatureState,
  selectFlightsCopy,
  selectCurrentCultureCode,
  selectSelectedDates,
  selectRouteSchedule,
  selectStationsState,
  selectAvailabilityTripTypeState,
  (state, flightsCopy, cultureCode, selectedDates, routeSchedule, stations, tripType) => {
    if (state.searchInput && state.lowFareAvailability && flightsCopy && cultureCode){
      const flightAvailabilities: FilghtAvailability[] = [];
      const cabinClass = state.selectedCabinClass ?? state.preferredCabinClass;

      state.searchInput.criteria.forEach((criteria, index) => {
        const routeDates = routeSchedule?.[criteria.origin + '-' + criteria.destination]?.filter(s => s.isDohop === false) ?? [];
        const lowFareAmounts = state.lowFareAvailability?.[index]?.cabins.filter(c => c.cabinName.startsWith('E'))[0]?.lowFareAmounts ?? [];
        const flightAvailability: FilghtAvailability = createFlightAvailability(index, flightsCopy, selectedDates, lowFareAmounts, cultureCode, criteria, cabinClass, routeDates, stations, tripType);
        flightAvailabilities.push(flightAvailability);
      });
      return flightAvailabilities
    }
    return null
  }
)

export const selectMaxAvailableDate = createSelector(
  selectAppCmsCopy,
  (appCopy) => {
    if (appCopy) {
      if (isNaN(+appCopy.searchWidgetFull.scheduleLoadedThruDays)) {
        return DateTime.fromFormat(appCopy.searchWidgetFull.scheduleLoadedThruDate, DATE_FORMAT_YEAR_MONTH_DATE);
      } else {
        const thruDays = DateTime.now()
          .plus({ days: +appCopy.searchWidgetFull.scheduleLoadedThruDays })
          .startOf('day');
        const thruDate = DateTime.fromFormat(
          appCopy.searchWidgetFull.scheduleLoadedThruDate,
          DATE_FORMAT_YEAR_MONTH_DATE,
        ).startOf('day');

        return thruDays < thruDate ? thruDays : thruDate;
      }
    }
    return null
  }
)

export const selectFlightsAvailabilityFaresTotal = (includeInfantOnLapFee: boolean, strikethrough: boolean = false) => createSelector(
  selectAvailabilitySelectedFaresState,
  selectSearchInputPassengerState,
  selectAvailabilityInfantInLapFee,
  selectBooking,
  (
    fares,
    passengers,
    infantInLapFee,
    booking
  ) => {
    if (!fares || !passengers || infantInLapFee === undefined) {
      return 0;
    }


    const infantInLapCount = includeInfantOnLapFee ? countAvailabilityPassengersByType(passengers, PassengerTypeCode.INF) : 0;
    const noneInfantOnLapPassengers = countAvailabilityPassengersExcludingType(passengers, PassengerTypeCode.INF)

    const totalPublished = fares.reduce((acc, cur) => {
      if (!cur) return acc;

      const noneInfantOnLapFareTotal = cur.details.totals.publishedTotal * noneInfantOnLapPassengers;
      const infantFareTotal = infantInLapFee * infantInLapCount;
        return acc + noneInfantOnLapFareTotal + infantFareTotal;
    }, 0);

    const totalTaxes = fares.reduce((acc, cur) => {
      if (!cur) return acc;
        return acc + cur.details.totals.taxesAndFeesTotal;
    }, 0);

    if(!strikethrough) {
      return fares.reduce((acc, cur) => {
        if (!cur) return acc;

        const noneInfantOnLapFareTotal = cur.details.totals.fareTotal * noneInfantOnLapPassengers;
        const infantFareTotal = infantInLapFee * infantInLapCount;
        return acc + noneInfantOnLapFareTotal + infantFareTotal;
      }, 0);
    } else {
      const origin = booking?.journeys[0]?.designator.origin;
      const destination = booking?.journeys[0]?.designator.destination;
      if (origin && destination && booking.journeys.length > 1) {
        const route = `${origin}-${destination}`
        const discount = routeDiscountMap[route] ?? 0.20;
        return (totalPublished / (1 - discount)) + totalTaxes;
      }
    }
  }
);

export const selectFlightsAvailabilityFaresTotalRounded = (includeInfantOnLapFee: boolean, strikethrough: boolean = false) => createSelector(
  selectFlightsAvailabilityFaresTotal(includeInfantOnLapFee, strikethrough),
  Math.ceil
);

export const selectFlightsAvailabilityFaresTotalStrikethrough = (includeInfantOnLapFee: boolean) => createSelector(
  selectAvailabilitySelectedFaresState,
  selectSearchInputPassengerState,
  selectAvailabilityInfantInLapFee,
  (
    fares,
    passengers,
    infantInLapFee,
  ) => {
    if (!fares || !passengers || infantInLapFee === undefined) {
      return 0;
    }


    const infantInLapCount = includeInfantOnLapFee ? countAvailabilityPassengersByType(passengers, PassengerTypeCode.INF) : 0;
    const noneInfantOnLapPassengers = countAvailabilityPassengersExcludingType(passengers, PassengerTypeCode.INF)

    return fares.reduce((acc, cur) => {
      if (!cur) return acc;

      const noneInfantOnLapFareTotal = cur.details.totals.roundedStrikethroughTotal * noneInfantOnLapPassengers;
      const infantFareTotal = infantInLapFee * infantInLapCount;
      return acc + noneInfantOnLapFareTotal + infantFareTotal;
    }, 0);
  }
);

export const selectFlightsAvailabilityFaresTotalStrikethroughRounded = (includeInfantOnLapFee: boolean) => createSelector(
  selectFlightsAvailabilityFaresTotalStrikethrough(includeInfantOnLapFee),
  Math.ceil
);

export const selectRouteScheduleByCityPair = ({
  cityPair,
}: {
  cityPair: { origin: string; destination: string };
}) =>
  createSelector(
    selectRouteSchedule,
    (routeSchedule) =>
      routeSchedule?.[`${cityPair.origin}-${cityPair.destination}`],
  );

export const selectLowFareSummaries = createSelector(
  selectAvailabilityFeatureState,
  (state) => state.lowFareSummaries
)

export const selectTripOrigin = createSelector(
  selectSearchInputCriteriaState,
  (searchInputCriteria) => searchInputCriteria?.[0]?.origin ?? ''
)

export const selectTripDestination = createSelector(
  selectSearchInputCriteriaState,
  (searchInputCriteria) => searchInputCriteria?.[0]?.destination ?? ''
)

export const selectMetaId = createSelector(
  selectAvailabilityFeatureState,
  (state) => state.metaId
)

export const selectPremiumJourneyFaresBySelectedJourney = createSelector(
  selectAvailabilitySelectedJourneysState,
  selectActiveSegment,
  (journeys, selectedJourneyKey) => {
    return journeys
      ?.find((journey) => journey?.journeyKey === selectedJourneyKey)
      ?.fares.filter((fare) => fare.details.cabin === PREMIUM_CABIN);
  },
);

export const selectHasPremiumJourneyFaresBySelectedJourney = createSelector(
  selectPremiumJourneyFaresBySelectedJourney, 
  (premiumJourneyFares) => {
    return !!premiumJourneyFares?.length;
  },
);
